import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import * as S from './BusinessDetails.styles';
import * as C from '../../../Common.styles';
import getPincodeInfo from '@ibibo/get-pincode-info';
import {sendEvarData} from '@ibibo/analytics';
import {validations} from '../../../../../../utils/validations';
import {userCountrySelector} from '../../../../../../store/selectors';

const BusinessDetails = ({
  fieldValidators,
  data,
  errors,
  businessTypeList,
  updateBusinessDetails,
  gstEnabled,
  doNotHaveGSTIN: doNotHaveGSTINProp,
  onDoNotHaveGSTINChange,
}) => {
  const [
    {panInfo, businessType, address, state, city, pincode, gstNumber},
    setBusinessDetail,
  ] = useState({
    panInfo: data.panInfo || null,
    businessType: data.businessType || '',
    address: data.address || '',
    state: data.state || '',
    city: data.city || '',
    pincode: data.pincode || '',
    gstNumber: data.gstNumber || '',
  });

  useEffect(() => {
    setBusinessDetail({
      businessType: data.businessType || businessType,
      address: data.address || address,
      state: data.state || state,
      city: data.city || city,
      pincode: data.pincode || pincode,
      gstNumber: data.gstNumber || gstNumber,
      panInfo: data.panInfo || panInfo,
    });
  }, [data]);

  const [disabelCity, setDisableCity] = useState(false);
  const [pincodeDataLoading, setPincodeDataLoading] = useState(false);
  const [doNotHaveGSTINLocal, setDoNotHaveGSTINLocal] = useState(
    data.panUploaded && data.gstNumber === '' && !panInfo ? true : false,
  );
  const doNotHaveGSTIN =
    onDoNotHaveGSTINChange !== undefined ? doNotHaveGSTINProp : doNotHaveGSTINLocal;

  const userCountry = useSelector(userCountrySelector);

  const getHasGSTN = list => {
    for (let i = 0; i < list.length; i++) {
      if (list[i].active_status === 'Active') {
        return true;
      }
    }
    return false;
  };
  const hasGSTINList =
    panInfo && panInfo.gstin_list && panInfo.gstin_list.length
      ? getHasGSTN(panInfo.gstin_list)
      : false;

  const onPincodeBlur = async (key, val) => {
    let errorObj = {};
    if (fieldValidators[key]) {
      const validators = fieldValidators[key].validators;
      for (let i = 0; i < validators.length; i++) {
        const validateFunction = validations[validators[i]];
        const resp = validateFunction({
          value: val,
          fieldName: fieldValidators[key].fieldName,
        });
        if (resp && !errorObj[key]) {
          errorObj[key] = resp;
        }
      }
    }
    if (!Object.keys(errorObj).length) {
      setPincodeDataLoading(true);
      const resp = await getPincodeInfo(val);
      if (resp) {
        const dataObj = {
          panInfo,
          gstNumber,
          businessType,
          address,
          city: resp?.data?.District || '',
          state: resp?.data?.StateName || '',
          pincode,
        };
        setBusinessDetail(dataObj);
        setPincodeDataLoading(false);
        updateBusinessDetails(dataObj);
      }
      if (resp.success) {
        setDisableCity(true);
      }
      if (!resp.success) {
        setDisableCity(false);
      }
    }
  };

  const toggleCheckBox = event => {
    let evarStr = '';
    const isChecked = event.target.checked;
    if (onDoNotHaveGSTINChange) {
      onDoNotHaveGSTINChange(isChecked);
    } else {
      setDoNotHaveGSTINLocal(isChecked);
    }
    if (isChecked) {
      evarStr = 'myPartner|signup|businessVerification|GSTN|checked';
    } else {
      evarStr = 'myPartner|signup|businessVerification|GSTN|unchecked';
    }
    sendEvarData(
      {
        prop54: evarStr,
      },
      true,
    );
  };

  const GSTINSlectBox = ({list}) => (
    <C.CommonSelect
      value={gstNumber}
      onChange={event => onChangeHandler('gstNumber', event.target.value)}
      hasError={errors['gstNumber'] ? true : false}
    >
      <option value="" disabled hidden>
        Select GST Number
      </option>
      {list.map(item => {
        if (item.active_status === 'Active') {
          return (
            <option key={item.gstin} value={item.gstin}>
              {item.gstin} {item.state}
            </option>
          );
        }
        return null;
      })}
    </C.CommonSelect>
  );

  const BusinessTypeSelect = () => (
    <C.CommonSelect
      onChange={event => onChangeHandler('businessType', event.target.value)}
      value={businessType}
      hasError={errors['businessType'] ? true : false}
    >
      <option value="" disabled hidden>
        Select Business Type
      </option>
      {Object.keys(businessTypeList).map(key => (
        <option key={key} value={key}>
          {businessTypeList[key]}
        </option>
      ))}
    </C.CommonSelect>
  );

  const onChangeHandler = (field, value) => {
    const data = {
      panInfo,
      businessType,
      address,
      state,
      city,
      pincode,
      gstNumber,
    };
    data[field] = value;
    setBusinessDetail(data);
    updateBusinessDetails(data);
  };

  useEffect(() => {
    sendEvarData(
      {
        prop54: `myPartner|signup|businessVerification|gstnUser|${hasGSTINList ? 'true' : 'false'}`,
      },
      true,
    );
  }, []);

  return (
    <S.BusinessDetailsWrap disabled={!gstEnabled}>
      <S.BusinessDetailsInfoWrap>
        <S.BusinessDetailsInfoHeadWrap>
          <span />
          <S.BusinessDetailsInfoHeadText>Business Details</S.BusinessDetailsInfoHeadText>
        </S.BusinessDetailsInfoHeadWrap>
      </S.BusinessDetailsInfoWrap>
      <S.BusinessDetailMainWrap>
        <S.GstBlockWrap>
          <S.KycNumberWrap>
            <C.CommonLabel>GSTIN Number</C.CommonLabel>
            <C.CommonInputWrap>
              {hasGSTINList && !doNotHaveGSTIN ? (
                <GSTINSlectBox list={panInfo.gstin_list} />
              ) : hasGSTINList ? (
                <C.CommonInput
                  isGST
                  disabled={doNotHaveGSTIN}
                  type="text"
                  name="gstNumber"
                  placeholder="GSTIN Number"
                  value={gstNumber}
                  onChange={event => onChangeHandler('gstNumber', event.target.value)}
                  hasError={errors['gstNumber'] ? true : false}
                />
              ) : null}
              <C.ErrorText>{errors['gstNumber']}</C.ErrorText>
            </C.CommonInputWrap>
          </S.KycNumberWrap>
          <S.NoKycCheckWrap>
            <S.GstDisabelWrap>
              <S.GstInCheckBox
                type="checkbox"
                checked={doNotHaveGSTIN}
                onChange={toggleCheckBox}
              ></S.GstInCheckBox>
              <S.GstDisableText>Don't have GSTIN</S.GstDisableText>
            </S.GstDisabelWrap>
          </S.NoKycCheckWrap>
        </S.GstBlockWrap>
        {(!gstNumber && !doNotHaveGSTIN && !hasGSTINList && (
          <S.NoGstnMsg>
            No active GSTIN found against the PAN. Please select Don't have
            GSTIN
          </S.NoGstnMsg>
        )) ||
          null}
        {(gstNumber || doNotHaveGSTIN || hasGSTINList) && (
          <>
            <C.InputWrapper>
              <C.CommonInputWrap>
                <C.CommonLabel>Business Type</C.CommonLabel>
                {businessTypeList ? (
                  <BusinessTypeSelect />
                ) : (
                  <C.CommonInput
                    type="text"
                    name="businessType"
                    placeholder="Business Type"
                    value={businessType}
                    onChange={event => onChangeHandler('businessType', event.target.value)}
                    hasError={errors['businessType'] ? true : false}
                  />
                )}
                <C.ErrorText>{errors['businessType']}</C.ErrorText>
              </C.CommonInputWrap>
            </C.InputWrapper>
            <C.InputWrapper>
              <C.CommonInputWrap>
                <C.CommonLabel>Agency Address</C.CommonLabel>
                <C.CommonInput
                  type="text"
                  name="address"
                  placeholder="Address"
                  value={address}
                  onChange={event => onChangeHandler('address', event.target.value)}
                  hasError={errors['address'] ? true : false}
                />
                <C.ErrorText>{errors['address']}</C.ErrorText>
              </C.CommonInputWrap>
            </C.InputWrapper>
            <C.InputWrapper>
              <S.StateCityWrapper>
                <C.CommonInputWrap>
                  <C.CommonLabel>Pincode</C.CommonLabel>
                  <S.InputPincode
                    type="text"
                    name="pincode"
                    placeholder="Pincode"
                    value={pincode}
                    onChange={event => onChangeHandler('pincode', event.target.value)}
                    onBlur={event => onPincodeBlur('pincode', event.target.value)}
                    hasError={errors['pincode'] ? true : false}
                    maxLength={6}
                  />
                  <C.ErrorText>{errors['pincode']}</C.ErrorText>
                </C.CommonInputWrap>

                <C.CommonInputWrap>
                  <C.CommonLabel>City</C.CommonLabel>
                  <S.InputCity
                    type="text"
                    name="city"
                    placeholder="City"
                    value={city}
                    disabled={disabelCity}
                    onChange={event => onChangeHandler('city', event.target.value)}
                    hasError={errors['city'] ? true : false}
                  />
                  <C.ErrorText>{errors['city']}</C.ErrorText>
                </C.CommonInputWrap>

                <C.CommonInputWrap>
                  <C.CommonLabel>State</C.CommonLabel>
                  <S.InputState
                    type="text"
                    name="state"
                    placeholder="State"
                    value={state}
                    disabled={disabelCity}
                    onChange={event => onChangeHandler('state', event.target.value)}
                    hasError={errors['state'] ? true : false}
                  />
                  <C.ErrorText>{errors['state']}</C.ErrorText>
                </C.CommonInputWrap>
              </S.StateCityWrapper>
            </C.InputWrapper>
          </>
        )}
      </S.BusinessDetailMainWrap>
    </S.BusinessDetailsWrap>
  );
};

export default BusinessDetails;
